/**
 * Resolves a book for the Book Details route from just an id, in layered
 * precedence: an already-shelved book (richest local data) → the local
 * seed/discoverable catalog → whatever the click site had in hand (`hint`,
 * passed via `navigate(bookHref(book), { state: { hint: book } })`) →
 * nothing found. `hint` also hydrates title/author for the id-less legacy
 * mock arrays in data.js (see utils/resolveMockBookId.js).
 *
 * Separately, whatever book resolves gets enriched with Open Library data
 * it doesn't already have — a description, ebook availability, and (only
 * when nothing local matched at all) title/cover/subjects. A shelved or
 * seed-catalog book never carries a description or an Open Library work id
 * of its own, so this always does a one-off title+author search to find
 * the matching work, then fetches that work's details — the same
 * enrichment pattern BookCoverArt/useBookCoverLookup already uses for
 * covers, just for descriptions too. Whatever resolves is normalized into
 * one superset shape (toDetailBook) so every section component only ever
 * deals with one Book shape.
 */

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useBookshelf } from '@/components/bookshelf';
import { useAuth } from '@/lib/useAuth';
import { getBookById } from '@/lib/localBookCatalog';
import { extractOpenLibraryWorkId, pickTone } from '@/lib/bookModel';
import { getBookDetails, getWorkDetails, getEbookAvailability, searchBooks } from '@/services/openLibraryService';

function toDetailBook(raw) {
  if (!raw) return null;
  return {
    id: raw.id,
    title: raw.title ?? 'Untitled',
    authors: raw.authors ?? (raw.author ? [raw.author] : []),
    coverUrl: raw.coverUrl ?? null,
    tone: raw.tone ?? pickTone(raw.id ?? raw.title),
    source: raw.source ?? null,
    subjects: raw.subjects ?? raw.genres ?? [],
    publishYear: raw.publishYear ?? raw.firstPublishYear ?? null,
    publisher: raw.publisher ?? null,
    description: raw.description ?? null,
    isbn10: raw.isbn10 ?? [],
    isbn13: raw.isbn13 ?? [],
    openLibraryWorkId: raw.openLibraryWorkId ?? null,
    ebookAccess: raw.ebookAccess ?? 'no_ebook',
    internetArchiveId: raw.internetArchiveId ?? null,
    totalPages: raw.totalPages ?? null,
    currentPage: raw.currentPage ?? null,
    rating: raw.rating ?? null,
    status: raw.status ?? null,
    isFavorite: raw.isFavorite ?? false,
    notes: raw.notes ?? null,
    friendsReading: raw.friendsReading ?? 0,
    dateAdded: raw.dateAdded ?? null,
    dateStarted: raw.dateStarted ?? null,
    dateFinished: raw.dateFinished ?? null,
  };
}

export function useBookDetails(bookId, hint) {
  const { books } = useBookshelf();
  const { accessToken } = useAuth();

  const shelfBook = useMemo(() => books.find((b) => b.id === bookId) ?? null, [books, bookId]);
  const catalogBook = useMemo(() => (shelfBook ? null : getBookById(bookId)), [shelfBook, bookId]);
  const urlWorkId = useMemo(
    () => (shelfBook || catalogBook ? null : extractOpenLibraryWorkId(bookId)),
    [shelfBook, catalogBook, bookId]
  );
  const isMockId = Boolean(bookId?.startsWith('mock_')) && !shelfBook && !catalogBook && !urlWorkId;

  let source = null;
  let localRaw = null;
  if (shelfBook) {
    source = 'shelf';
    localRaw = shelfBook;
  } else if (catalogBook) {
    source = 'catalog';
    localRaw = catalogBook;
  } else if (hint) {
    source = urlWorkId ? 'openlibrary' : 'hint';
    localRaw = { id: bookId, openLibraryWorkId: urlWorkId ?? hint.openLibraryWorkId ?? null, ...hint };
  }

  const bookQuery = useQuery({
    queryKey: ['book-details', bookId, accessToken],
    queryFn: ({ signal }) => getBookDetails(bookId, { signal, accessToken }),
    enabled: Boolean(bookId),
    staleTime: 5 * 60_000,
    retry: 0,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });

  const remoteBook = bookQuery.data ?? null;

  const knownWorkId = urlWorkId || localRaw?.openLibraryWorkId || null;
  const knownTitle = localRaw?.title ?? null;
  const knownAuthor = localRaw?.author ?? localRaw?.authors?.[0] ?? null;

  // No known Open Library work id yet, and no local description — search
  // for one by title(+author). Covers shelf/catalog books (never carry an
  // OL id at all), hint-only books without one, and a cold mock_ visit with
  // no hint (derive a query from the id's own slug).
  const searchQueryText = (!knownWorkId && !localRaw?.description)
    ? knownTitle
      ? `${knownTitle} ${knownAuthor ?? ''}`.trim()
      : isMockId
        ? bookId.replace(/^mock_/, '').replace(/_/g, ' ').trim()
        : null
    : null;

  // retry:0 + refetchOnMount/WindowFocus:false, matching useBookCoverLookup's
  // reasoning exactly: a slow/unreachable network should fail fast to "no
  // description"/"no Read/Borrow button" instead of silently retrying 3x
  // with backoff — which is also what was showing up as canceled requests
  // in devtools (a retry-in-flight interrupted by the next re-render).
  const enrichSearch = useQuery({
    queryKey: ['openlibrary-enrich-search', searchQueryText],
    queryFn: ({ signal }) => searchBooks(searchQueryText, { limit: 1, signal }),
    enabled: Boolean(searchQueryText),
    staleTime: 10 * 60_000,
    retry: 0,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });

  const foundBook = enrichSearch.data?.books?.[0] ?? null;
  const resolvedWorkId = knownWorkId || foundBook?.openLibraryWorkId || null;

  const workQuery = useQuery({
    queryKey: ['openlibrary-work', resolvedWorkId],
    queryFn: ({ signal }) => getWorkDetails(resolvedWorkId, { signal }),
    enabled: Boolean(resolvedWorkId) && !localRaw?.description,
    staleTime: 5 * 60_000,
    retry: 0,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });

  const ebookQuery = useQuery({
    queryKey: ['openlibrary-ebook-availability', resolvedWorkId],
    queryFn: ({ signal }) => getEbookAvailability(resolvedWorkId, { signal }),
    enabled: Boolean(resolvedWorkId) && !localRaw?.internetArchiveId && !foundBook?.internetArchiveId,
    staleTime: 10 * 60_000,
    retry: 0,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });

  let raw = null;
  if (remoteBook || localRaw) {
    const localSubjects = localRaw?.subjects?.length ? localRaw.subjects : localRaw?.genres?.length ? localRaw.genres : null;
    raw = {
      ...localRaw,
      ...remoteBook,
      id: remoteBook?.id ?? localRaw?.id ?? bookId,
      title: remoteBook?.title ?? localRaw?.title,
      authors: remoteBook?.authors?.length ? remoteBook.authors : localRaw?.authors,
      coverUrl: remoteBook?.coverUrl ?? localRaw?.coverUrl ?? foundBook?.coverUrl ?? null,
      subjects: remoteBook?.subjects?.length ? remoteBook.subjects : localSubjects ?? foundBook?.subjects ?? [],
      description: remoteBook?.description ?? localRaw?.description ?? workQuery.data?.description ?? null,
      openLibraryWorkId: remoteBook?.openLibraryWorkId ?? localRaw?.openLibraryWorkId ?? resolvedWorkId ?? null,
      publishYear: remoteBook?.publishYear ?? localRaw?.publishYear ?? localRaw?.firstPublishYear ?? foundBook?.publishYear ?? null,
      publisher: remoteBook?.publisher ?? localRaw?.publisher ?? foundBook?.publisher ?? null,
      isbn10: remoteBook?.isbn10?.length ? remoteBook.isbn10 : localRaw?.isbn10?.length ? localRaw.isbn10 : foundBook?.isbn10 ?? [],
      isbn13: remoteBook?.isbn13?.length ? remoteBook.isbn13 : localRaw?.isbn13?.length ? localRaw.isbn13 : foundBook?.isbn13 ?? [],
      ebookAccess: remoteBook?.ebookAccess ?? localRaw?.ebookAccess ?? foundBook?.ebookAccess ?? ebookQuery.data?.ebookAccess ?? 'no_ebook',
      internetArchiveId: remoteBook?.internetArchiveId ?? localRaw?.internetArchiveId ?? foundBook?.internetArchiveId ?? ebookQuery.data?.internetArchiveId ?? null,
    };
  } else if (resolvedWorkId) {
    // Nothing local matched at all (a raw ol_<workId> URL with no hint, or
    // a mock_/hint id that only Open Library search resolved) — the book
    // is built entirely from Open Library data.
    source = 'openlibrary';
    raw = {
      id: bookId,
      title: foundBook?.title ?? workQuery.data?.title ?? null,
      authors: foundBook?.authors ?? [],
      coverUrl: foundBook?.coverUrl ?? workQuery.data?.coverUrl ?? null,
      subjects: foundBook?.subjects?.length ? foundBook.subjects : workQuery.data?.subjects ?? [],
      description: workQuery.data?.description ?? null,
      openLibraryWorkId: resolvedWorkId,
      publishYear: foundBook?.publishYear ?? null,
      publisher: foundBook?.publisher ?? null,
      isbn10: foundBook?.isbn10 ?? [],
      isbn13: foundBook?.isbn13 ?? [],
      ebookAccess: foundBook?.ebookAccess ?? ebookQuery.data?.ebookAccess ?? 'no_ebook',
      internetArchiveId: foundBook?.internetArchiveId ?? ebookQuery.data?.internetArchiveId ?? null,
    };
  }

  const stillResolving =
    !raw &&
    ((Boolean(searchQueryText) && enrichSearch.isLoading) ||
      (Boolean(resolvedWorkId) && !foundBook && workQuery.isLoading));

  const status = raw ? 'success' : stillResolving ? 'loading' : 'not-found';
  const book = useMemo(() => toDetailBook(raw), [raw]);

  return {
    book,
    status,
    isShelved: Boolean(shelfBook),
    shelfBook,
    source,
  };
}
